import { useEffect, useState, useCallback, useRef, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { authService } from '@/services/auth';
import { auth } from '@/services/firebase/config';
import { signInAnonymously } from 'firebase/auth';
import { GameSession, EmpiricalStudyConsentChoice } from '@/types';
import { useCurrentSession, useSessionActions } from '@/stores/gameSessionStore';
import { useGameStore } from '@/stores/gameStore';
import FluffyCreatureAvatar from '@/components/FluffyCreatureAvatar';
import FluffyCreatureStyles from '@/components/FluffyCreatureStyles';
import HowToPlayModal from '@/components/HowToPlayModal';
import { createStudentUrl, debugUrlState, getStudentRouteForGameState } from '@/utils/studentUrlHelpers';
import { storeSessionData } from '@/utils/sessionRecovery';
import {
  storeEmpiricalStudySession,
  getStoredEmpiricalStudySession,
  updateEmpiricalStudySession
} from '@/utils/empiricalStudySession';
import { urlMonitor } from '@/utils/urlDiagnostics';
import { audioManager } from '@/utils/audioManager';
import { getCharacterSound } from '@/config/soundEffects';
import { CHARACTERS } from '@/constants/characters';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { dbService } from '@/services/database';
import {
  EMPIRICAL_STUDY_DECLARATION,
  PRE_GAME_SURVEY_QUESTIONS
} from '@/config/empiricalStudy';
import { empiricalStudyService } from '@/services/study/empiricalStudyService';

const JoinGame = () => {
  const { gameCode } = useParams<{ gameCode: string }>();
  const navigate = useSafeNavigate();
  const [gameSession, setGameSession] = useState<GameSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playerName, setPlayerName] = useState('');
  const [selectedCharacter, setSelectedCharacter] = useState<number | null>(null);
  const [isJoiningAndReady, setIsJoiningAndReady] = useState(false);
  const [isStudentReady, setIsStudentReady] = useState(false);
  const [isHowToPlayOpen, setIsHowToPlayOpen] = useState(false);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [studyConsentChoice, setStudyConsentChoice] = useState<EmpiricalStudyConsentChoice | null>(null);
  const [preSurveyResponses, setPreSurveyResponses] = useState<Record<string, string>>({});
  const currentSession = useCurrentSession();
  const { setCurrentSessionDirect } = useSessionActions();
  const {
    setSelectedCharacter: storeSetSelectedCharacter,
    initializeGame,
    hydrateSessionPlayers,
    setCurrentPlayer,
    setCurrentPlayerId
  } = useGameStore();

  const updateStudentUrl = useCallback((sessionId: string, newPlayerId?: string | null) => {
    if (!gameCode) return;

    const path = `/join-game/${gameCode}`;
    const targetUrl = createStudentUrl(path, sessionId, newPlayerId || undefined);
    if (typeof window !== 'undefined') {
      const currentUrl = `${window.location.pathname}${window.location.search}`;
      if (currentUrl === targetUrl) {
        return;
      }
    }

    // Ensure React Router sees the URL change so hooks depending on location update immediately
    navigate(targetUrl, { replace: true });
  }, [gameCode, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const playerIdParam = params.get('playerId');
    if (playerIdParam) {
      setPlayerId(playerIdParam);
    }

    // Start URL monitoring for this session
    urlMonitor.start(500);

    return () => {
      urlMonitor.stop();
    };
  }, []);

  useEffect(() => {
    if (playerId && gameSession) {
      const player = gameSession.players.find(p => p.id === playerId);
      setIsStudentReady(Boolean(player?.isReady));
    }
  }, [playerId, gameSession]);

  useEffect(() => {
    if (!currentSession || !gameSession || currentSession.id !== gameSession.id) {
      return;
    }

    setGameSession(currentSession);
  }, [currentSession, gameSession]);

  // Direct Firebase subscription to detect when teacher starts the game.
  // This bypasses all React effect timing issues by listening to Firebase directly.
  const hasNavigatedRef = useRef(false);
  useEffect(() => {
    if (!isStudentReady || !playerId || !gameSession) {
      return;
    }

    hasNavigatedRef.current = false;
    const storedStudySession = getStoredEmpiricalStudySession(gameSession.id);
    let hasRecordedStudyStart = Boolean(storedStudySession?.gameStartedAt);

    const unsubscribe = dbService.subscribeToGameSession(gameSession.id, (updatedSession) => {
      if (hasNavigatedRef.current) return;

      const newState = updatedSession.gameState;
      if (newState && newState !== 'lobby') {
        if (updatedSession.empiricalStudyEnabled && !hasRecordedStudyStart) {
          const currentStudySession = getStoredEmpiricalStudySession(updatedSession.id);
          if (
            currentStudySession?.participantCode &&
            currentStudySession.consentChoice === 'consent'
          ) {
            hasRecordedStudyStart = true;
            const startedAt = new Date().toISOString();
            updateEmpiricalStudySession({
              sessionId: updatedSession.id,
              gameStartedAt: startedAt
            });
            empiricalStudyService.markGameStarted(currentStudySession.participantCode).catch((studyError) => {
              console.error('❌ Failed to record empirical study game start:', studyError);
            });
          }
        }

        hasNavigatedRef.current = true;

        // Update the store so other components have the latest session
        setCurrentSessionDirect(updatedSession);

        // Initialize game store with questions if available
        if (updatedSession.questions?.length > 0) {
          initializeGame(updatedSession.id, updatedSession.questions);
        }

        const targetPath = getStudentRouteForGameState(newState);
        if (targetPath) {
          const targetUrl = createStudentUrl(targetPath, updatedSession.id, playerId);
          navigate(targetUrl);
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [isStudentReady, playerId, gameSession?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Character options matching PlayerSetup
  const characters = CHARACTERS;
  const isEmpiricalStudySession = Boolean(gameSession?.empiricalStudyEnabled);
  const hasConsentedToStudy = studyConsentChoice === 'consent';
  const hasCompletedPreSurvey = PRE_GAME_SURVEY_QUESTIONS.every(
    (question) => Boolean(preSurveyResponses[question.id])
  );
  const hasCompletedRequiredStudyQuestions = !hasConsentedToStudy || hasCompletedPreSurvey;

  const ensureAuthenticated = async () => {
    try {
      // Check if user is already authenticated
      if (!auth.currentUser) {
        await signInAnonymously(auth);
      }
    } catch (error) {
      console.error('❌ Anonymous authentication failed:', error);

      // Check for specific Firebase auth errors
      if (error instanceof Error) {
        if (error.message.includes('auth/admin-restricted-operation')) {
          throw new Error('Anonymous authentication is disabled. Please contact the teacher to enable guest access.');
        } else if (error.message.includes('auth/network-request-failed')) {
          throw new Error('Network connection failed. Please check your internet connection.');
        }
      }

      throw new Error('Authentication failed. Please try again or contact support.');
    }
  };

  const findGameSession = useCallback(async (code: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // First, try to find a multiplayer game session
      const session = await authService.getGameSession(code);

      if (session) {
        setGameSession(session);

        if (playerId) {
          const player = session.players.find(p => p.id === playerId);
          if (player?.isReady) {
            setIsStudentReady(true);
          }
        }
      } else {
        // If not found, check if it's an AI game session
        const aiSession = await authService.getAiGameSession(code, true);

        if (aiSession) {
          if (aiSession.status === 'expired') {
            setError('This game has expired. Please contact your teacher for a new code.');
          } else if (aiSession.status === 'cancelled') {
            setError('This game has been cancelled. Please contact your teacher.');
          } else {
            // AI game found - redirect to AI setup with pre-loaded question set
            setIsLoading(false);
            navigate(`/ai-setup?aiGameId=${aiSession.id}&gameCode=${aiSession.gameCode}`);
            return;
          }
        } else {
          setError('Game not found. Please check the game code and try again.');
        }
      }
    } catch (err: unknown) {
      console.error('Error finding game:', err);
      setError('Failed to find game. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [playerId, navigate]);

  useEffect(() => {
    if (gameCode) {
      ensureAuthenticated()
        .then(() => {
          findGameSession(gameCode);
        })
        .catch((authError) => {
          console.error('❌ Authentication failed, cannot search for game:', authError);
          setError(authError instanceof Error ? authError.message : 'Unable to connect to the game service. Please try again.');
          setIsLoading(false);
        });
    }
  }, [gameCode, findGameSession]);

  const handleJoinAndReady = async (e: FormEvent) => {
    e.preventDefault();
    if (!playerName.trim() || !gameSession || selectedCharacter === null) return;
    if (
      isEmpiricalStudySession &&
      (!studyConsentChoice || !hasCompletedRequiredStudyQuestions)
    ) return;

    try {
      setIsJoiningAndReady(true);

      const authUid = auth.currentUser?.uid;
      if (isEmpiricalStudySession && !authUid) {
        throw new Error('Unable to create the study record. Please refresh and try again.');
      }

      const joinData = {
        gameCode: gameSession.gameCode,
        playerName: playerName.trim(),
        characterIndex: selectedCharacter,
        authUid
      };

      // Step 1: Join the game session
      const updatedSession = await authService.joinGameSession(gameSession.id, joinData);

      // Find the player ID that was just created
      const newPlayer = updatedSession.players.find(p => p.name === playerName.trim());
      const newPlayerId = newPlayer?.id;

      if (!newPlayerId) {
        throw new Error('Failed to get player ID after joining');
      }

      if (isEmpiricalStudySession) {
        try {
          const studyPreSurveyResponses = hasConsentedToStudy
            ? preSurveyResponses
            : {};
          const participantCode = await empiricalStudyService.createParticipantRecord({
            session: updatedSession,
            authUid: authUid || '',
            playerId: newPlayerId,
            playerName: playerName.trim(),
            consentChoice: studyConsentChoice || 'decline',
            preSurveyResponses: studyPreSurveyResponses
          });
          const storedAt = new Date().toISOString();
          storeEmpiricalStudySession({
            sessionId: updatedSession.id,
            gameCode: updatedSession.gameCode,
            participantCode,
            playerId: newPlayerId,
            playerName: hasConsentedToStudy ? playerName.trim() : '',
            consentChoice: studyConsentChoice || 'decline',
            preSurveyResponses: studyPreSurveyResponses,
            preSurveySubmittedAt: hasConsentedToStudy ? storedAt : undefined,
            lastUpdatedAt: storedAt
          });
        } catch (studyError) {
          try {
            await authService.leaveGameSession(gameSession.id, newPlayerId);
          } catch (leaveError) {
            console.error('❌ Failed to remove player after study setup error:', leaveError);
          }

          throw studyError;
        }
      }

      // Step 2: Immediately mark player as ready (single action from student perspective)
      await authService.setPlayerReadyStatus(gameSession.id, newPlayerId, true);

      // Store character selection in game store
      storeSetSelectedCharacter(selectedCharacter);

      // Initialize the game store with session data for proper synchronization
      if (updatedSession.questions && updatedSession.questions.length > 0) {
        initializeGame(updatedSession.id, updatedSession.questions);
      }

      // Update players list with ready status
      const updatedPlayersWithReady = updatedSession.players.map(p =>
        p.id === newPlayerId ? { ...p, isReady: true } : p
      );
      const readySession = {
        ...updatedSession,
        players: updatedPlayersWithReady
      };

      hydrateSessionPlayers(updatedPlayersWithReady, newPlayerId);

      if (newPlayer) {
        setCurrentPlayer({ ...newPlayer, isReady: true });
      } else {
        setCurrentPlayerId(newPlayerId);
      }

      // Update local state to show waiting screen
      setPlayerId(newPlayerId);
      setIsStudentReady(true);
      setGameSession(readySession);
      setCurrentSessionDirect(readySession);

      // Update URL to include sessionId for auto-navigation using helper
      updateStudentUrl(updatedSession.id, newPlayerId);
      debugUrlState('JoinGame - After Join and Ready');

      // Store session data for recovery
      storeSessionData({
        sessionId: updatedSession.id,
        playerId: newPlayerId,
        gameCode: gameCode || '',
        playerName: playerName.trim(),
        characterIndex: selectedCharacter,
        joinedAt: new Date()
      });

    } catch (err: unknown) {
      console.error('❌ Failed to join game and mark ready:', err);
      setError(err instanceof Error ? err.message : 'Failed to join game. Please try again.');
      // Reset states on error
      setIsStudentReady(false);
      setPlayerId(null);
    } finally {
      setIsJoiningAndReady(false);
    }
  };

  const handleCharacterSelect = (characterId: number) => {
    if (isStudentReady) return;

    setSelectedCharacter(characterId);

    // Play character selection sound
    const characterSound = getCharacterSound(characterId);
    if (characterSound && audioManager.isReady()) {
      audioManager.play(characterSound.id);
    }
  };

  if (isLoading) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.1)',
          padding: '3rem',
          borderRadius: '20px',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          maxWidth: '500px',
          width: '90%'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Finding Game...</h2>
          <p style={{ color: 'rgba(255,255,255,0.8)' }}>
            Searching for game with code: <strong>{gameCode}</strong>
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.1)',
          padding: '3rem',
          borderRadius: '20px',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          maxWidth: '500px',
          width: '90%'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#FF6B6B' }}>Game Not Found</h2>
          <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '2rem' }}>
            {error}
          </p>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '1rem 2rem',
              fontSize: '1.1rem',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!gameSession) {
    return null;
  }

  // Show waiting screen if student is ready
  const readyPlayerName = playerName ||
    (playerId ? gameSession.players.find(p => p.id === playerId)?.name : '') ||
    'Player';

  if (isStudentReady) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.1)',
          padding: '3rem',
          borderRadius: '20px',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          maxWidth: '500px',
          width: '90%'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem', animation: 'pulse 2s infinite' }}>⏳</div>
          <h2 style={{ fontSize: '1.8rem', marginBottom: '1rem' }}>Ready to Play!</h2>
          <p style={{ fontSize: '1.2rem', marginBottom: '2rem', color: 'rgba(255,255,255,0.9)' }}>
            You're all set! Waiting for your teacher to start the game...
          </p>

          {/* Show selected character */}
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.15)',
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h4 style={{ marginBottom: '1rem', color: '#FFD700' }}>Your Character</h4>
            {selectedCharacter !== null ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <FluffyCreatureAvatar
                  primaryColor={characters[selectedCharacter].primaryColor}
                  secondaryColor={characters[selectedCharacter].secondaryColor}
                  accentColor={characters[selectedCharacter].accentColor}
                  scale={1.2}
                  style={{ marginBottom: '1rem' }}
                />
                <p style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                  {characters[selectedCharacter].name}
                </p>
              </div>
            ) : (
              <p style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>No Character</p>
            )}
          </div>

          <div style={{
            backgroundColor: 'rgba(76, 175, 80, 0.2)',
            padding: '1rem',
            borderRadius: '8px',
            border: '2px solid rgba(76, 175, 80, 0.4)',
            marginBottom: '2rem'
          }}>
            <p style={{ color: '#4CAF50', fontSize: '1rem', margin: 0 }}>
              ✅ Connected as: <strong>{readyPlayerName}</strong>
            </p>
          </div>

          <button
            onClick={() => setIsHowToPlayOpen(true)}
            style={{
              padding: '0.9rem 1.7rem',
              fontSize: '1.05rem',
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: '#FFFFFF',
              border: '2px solid rgba(255,255,255,0.35)',
              borderRadius: '999px',
              cursor: 'pointer',
              fontWeight: 'bold',
              marginBottom: '1.5rem',
              boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
              transition: 'all 0.3s ease'
            }}
          >
            🧭 How To Play
          </button>

          <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
            The game will start automatically when your teacher begins the session.
          </p>
        </div>

        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.1); }
          }
        `}</style>
        <FluffyCreatureStyles />
        <HowToPlayModal
          isOpen={isHowToPlayOpen}
          onClose={() => setIsHowToPlayOpen(false)}
        />
      </div>
    );
  }

  return (
    <div style={{
      padding: '2rem',
      textAlign: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <div style={{
        backgroundColor: 'rgba(255,255,255,0.1)',
        padding: '3rem',
        borderRadius: '20px',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        maxWidth: '700px',
        width: '90%'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎮</div>
        <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Join Game</h1>

        {/* Game Info */}
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.1)',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          textAlign: 'left'
        }}>
          <h3 style={{ color: '#FFD700', marginBottom: '1rem' }}>📋 Game Details</h3>
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            <p><strong>📚 Subject:</strong> {gameSession.questionSetName}</p>
            <p><strong>👩‍🏫 Teacher:</strong> {gameSession.teacherName}</p>
            <p><strong>👥 Players:</strong> {gameSession.players.length}/{gameSession.maxPlayers || 'Unlimited'}</p>
            <p><strong>🔢 Code:</strong> {gameSession.gameCode}</p>
            <p><strong>📊 Status:</strong>
              <span style={{
                color: gameSession.gameState === 'lobby' ? '#4CAF50' :
                       gameSession.gameState === 'question' ? '#FF9800' : '#757575',
                fontWeight: 'bold',
                marginLeft: '0.5rem'
              }}>
                {gameSession.gameState === 'lobby' ? 'WAITING' : gameSession.gameState.toUpperCase()}
              </span>
            </p>
          </div>
        </div>

        {/* Setup Form */}
        {gameSession.gameState === 'lobby' && !isStudentReady ? (
          <form onSubmit={handleJoinAndReady} style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            {/* Name Input */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', textAlign: 'left' }}>
                📝 Enter Your Name:
              </label>
              <input
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Your name"
                maxLength={20}
                style={{
                  width: '100%',
                  padding: '1rem',
                  fontSize: '1.2rem',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  border: '2px solid rgba(255,215,0,0.5)',
                  borderRadius: '8px',
                  outline: 'none',
                  color: '#333',
                  fontWeight: 'bold',
                  boxSizing: 'border-box'
                }}
                autoFocus
                required
                disabled={isJoiningAndReady}
              />
            </div>

            {/* Character Selection */}
            <div>
              <label style={{ display: 'block', marginBottom: '1rem', fontWeight: 'bold', textAlign: 'left' }}>
                🎭 Choose Your Creature:
              </label>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '1rem',
                maxWidth: '400px',
                margin: '0 auto 1rem'
              }}>
                {characters.map((character) => (
                  <div
                    key={character.id}
                    onClick={() => handleCharacterSelect(character.id)}
                    style={{
                      cursor: isJoiningAndReady ? 'not-allowed' : 'pointer',
                      padding: '1rem',
                      borderRadius: '16px',
                      border: selectedCharacter === character.id ? '4px solid #FFD700' : '2px solid rgba(255,255,255,0.3)',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      transition: 'all 0.3s ease',
                      transform: selectedCharacter === character.id ? 'scale(1.1)' : 'scale(1)',
                      boxShadow: selectedCharacter === character.id
                        ? '0 0 20px rgba(255,215,0,0.5)'
                        : '0 2px 8px rgba(0,0,0,0.2)',
                      opacity: isJoiningAndReady ? 0.6 : 1
                    }}
                  >
                    <FluffyCreatureAvatar
                      primaryColor={character.primaryColor}
                      secondaryColor={character.secondaryColor}
                      accentColor={character.accentColor}
                    />
                    <p style={{
                      fontSize: '0.8rem',
                      color: 'white',
                      textAlign: 'center',
                      margin: '0.5rem 0 0 0',
                      fontWeight: 'bold'
                    }}>
                      {character.name}
                    </p>
                  </div>
                ))}
              </div>
              {selectedCharacter !== null && (
                <p style={{ textAlign: 'center', color: '#FFD700', fontWeight: 'bold' }}>
                  ✨ Selected: {characters[selectedCharacter].name}
                </p>
              )}
            </div>

            {isEmpiricalStudySession && (
              <div style={{
                display: 'grid',
                gap: '1.5rem',
                textAlign: 'left'
              }}>
                <div style={{
                  backgroundColor: 'rgba(34,197,94,0.18)',
                  border: '1px solid rgba(34,197,94,0.35)',
                  borderRadius: '14px',
                  padding: '1.25rem'
                }}>
                  <div style={{
                    color: '#BBF7D0',
                    fontWeight: 'bold',
                    marginBottom: '0.5rem'
                  }}>
                    WSU Empirical Study
                  </div>
                  <p style={{
                    margin: 0,
                    color: 'rgba(255,255,255,0.92)',
                    lineHeight: 1.5
                  }}>
                    {EMPIRICAL_STUDY_DECLARATION}
                  </p>
                </div>

                <div style={{
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  borderRadius: '14px',
                  padding: '1.25rem'
                }}>
                  <div style={{
                    fontWeight: 'bold',
                    marginBottom: '0.85rem',
                    fontSize: '1rem'
                  }}>
                    Consent
                  </div>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {[
                      {
                        value: 'consent' as const,
                        label: 'I consent to participate in the study'
                      },
                      {
                        value: 'decline' as const,
                        label: "I don't consent to participate in the study"
                      }
                    ].map((option) => (
                      <label
                        key={option.value}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.9rem 1rem',
                          borderRadius: '12px',
                          border: studyConsentChoice === option.value
                            ? '2px solid #FACC15'
                            : '1px solid rgba(255,255,255,0.18)',
                          backgroundColor: studyConsentChoice === option.value
                            ? 'rgba(250,204,21,0.18)'
                            : 'rgba(255,255,255,0.05)',
                          cursor: 'pointer'
                        }}
                      >
                        <input
                          type="radio"
                          name="study-consent"
                          value={option.value}
                          checked={studyConsentChoice === option.value}
                          onChange={() => setStudyConsentChoice(option.value)}
                          disabled={isJoiningAndReady}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {hasConsentedToStudy && PRE_GAME_SURVEY_QUESTIONS.map((question) => (
                  <div
                    key={question.id}
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.18)',
                      borderRadius: '14px',
                      padding: '1.25rem'
                    }}
                  >
                    <div style={{
                      fontWeight: 'bold',
                      marginBottom: '0.85rem',
                      fontSize: '1rem'
                    }}>
                      {question.prompt}
                    </div>
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                      {question.options.map((option) => (
                        <label
                          key={option.value}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '0.9rem 1rem',
                            borderRadius: '12px',
                            border: preSurveyResponses[question.id] === option.value
                              ? '2px solid #60A5FA'
                              : '1px solid rgba(255,255,255,0.18)',
                            backgroundColor: preSurveyResponses[question.id] === option.value
                              ? 'rgba(96,165,250,0.18)'
                              : 'rgba(255,255,255,0.05)',
                            cursor: 'pointer'
                          }}
                        >
                          <input
                            type="radio"
                            name={question.id}
                            value={option.value}
                            checked={preSurveyResponses[question.id] === option.value}
                            onChange={() => setPreSurveyResponses((prev) => ({
                              ...prev,
                              [question.id]: option.value
                            }))}
                            disabled={isJoiningAndReady}
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Single Action Button */}
            <button
              type="submit"
              disabled={
                !playerName.trim() ||
                selectedCharacter === null ||
                isJoiningAndReady ||
                (isEmpiricalStudySession &&
                  (!studyConsentChoice || !hasCompletedRequiredStudyQuestions))
              }
              style={{
                padding: '1.5rem 2.5rem',
                fontSize: '1.5rem',
                backgroundColor: (
                  !playerName.trim() ||
                  selectedCharacter === null ||
                  isJoiningAndReady ||
                  (isEmpiricalStudySession &&
                    (!studyConsentChoice || !hasCompletedRequiredStudyQuestions))
                )
                  ? 'rgba(255,255,255,0.3)' : '#FF6B35',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: (
                  !playerName.trim() ||
                  selectedCharacter === null ||
                  isJoiningAndReady ||
                  (isEmpiricalStudySession &&
                    (!studyConsentChoice || !hasCompletedRequiredStudyQuestions))
                )
                  ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                opacity: (
                  !playerName.trim() ||
                  selectedCharacter === null ||
                  isJoiningAndReady ||
                  (isEmpiricalStudySession &&
                    (!studyConsentChoice || !hasCompletedRequiredStudyQuestions))
                ) ? 0.5 : 1,
                boxShadow: (
                  !playerName.trim() ||
                  selectedCharacter === null ||
                  isJoiningAndReady ||
                  (isEmpiricalStudySession &&
                    (!studyConsentChoice || !hasCompletedRequiredStudyQuestions))
                )
                  ? 'none' : '0 4px 12px rgba(255,107,53,0.4)',
                transition: 'all 0.3s ease'
              }}
            >
              {isJoiningAndReady ? '🔄 Joining...' : "🚀 Let's DO THIS YOO!"}
            </button>
          </form>
        ) : (
          <div style={{
            backgroundColor: 'rgba(255,193,7,0.2)',
            padding: '1.5rem',
            borderRadius: '12px',
            border: '2px solid rgba(255,193,7,0.4)',
            marginBottom: '2rem'
          }}>
            <p style={{ color: '#FFD700', fontSize: '1.1rem' }}>
              This game is currently {gameSession.gameState} and not accepting new players.
            </p>
          </div>
        )}

        <button
          onClick={() => navigate('/')}
          style={{
            marginTop: '1rem',
            padding: '0.8rem 1.5rem',
            fontSize: '1rem',
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: 'white',
            border: '2px solid rgba(255,255,255,0.3)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          ← Back to Home
        </button>
        <FluffyCreatureStyles />
      </div>
    </div>
  );
};

export default JoinGame;
